import { useEffect, useState, useRef, useCallback } from "react";
import * as signalR from "@microsoft/signalr";
import Api from "./api";
import moment from "moment";
import { formatIntervalToReadable } from "../utils/breakReason";

// Tempo de debounce para batching de atualizações SignalR (ms)
const SIGNALR_BATCH_DELAY = 300;

// Detecta se a aba está visível
const isTabVisible = () => typeof document !== 'undefined' && !document.hidden;

const formatBreakItem = (item) => {
	if (!item) {
		return {
			id: null,
			startdateOriginal: null,
			breaklimitOriginal: null,
			startdate: '',
			enddate: '',
			breaklimit: '',
			breakduration: '',
			overflowtime: 'Não excedeu',
			userTeams: 'Sem time'
		};
	}

	const userTeams = item.user?.techinicalteamusers?.map(ut => ut?.techinicalteam?.name).filter(Boolean).join(", ") || 'Sem time';

	return {
		...item,
		startdateOriginal: item.startdate ?? null,
		breaklimitOriginal: item.breaklimit ?? null,
		startdate: item.startdate ? moment(item.startdate).format('DD/MM/YYYY HH:mm:ss') : '',
		enddate: item.enddate ? moment(item.enddate).format('DD/MM/YYYY HH:mm:ss') : '',
		breaklimit: formatIntervalToReadable(item.breaklimit),
		breakduration: formatIntervalToReadable(item.breakduration),
		overflowtime: item.overflowtime ? formatIntervalToReadable(item.overflowtime) : 'Não excedeu',
		userTeams
	}
};

/**
 * Hook para gerenciar conexão SignalR de pausas de trabalho
 * VERSÃO 2: Gerencia estado internamente e retorna os dados diretamente
 * Isso evita re-renders cascata no componente pai
 * 
 * IMPORTANTE: Quando a aba está em background, eventos SignalR são ignorados
 * e os dados são recarregados quando a aba volta ao foco.
 */
export function useBreakWorkHub(connection, intl, teamIdsQuery = null, teamIdsArray = null) {
	// Estado interno do hook - não depende de setState externo
	const [activeBreaks, setActiveBreaks] = useState([]);
	const [loading, setLoading] = useState(false);
	const [connected, setConnected] = useState(false);
	const [notification, setNotification] = useState(null);

	// Refs para evitar stale closures
	const stateRef = useRef({
		intl,
		teamIdsArray,
		teamIdsQuery,
		isMounted: true,
		isInitialized: false,
		needsRefresh: false, // Flag para recarregar quando aba voltar ao foco
	});

	// Refs para batching
	const pendingOpsRef = useRef({ toAdd: [], toUpdate: [], toRemove: [] });
	const batchTimeoutRef = useRef(null);

	// Atualiza refs quando props mudam
	useEffect(() => {
		stateRef.current.intl = intl;
		stateRef.current.teamIdsArray = teamIdsArray;
		stateRef.current.teamIdsQuery = teamIdsQuery;
	}, [intl, teamIdsArray, teamIdsQuery]);

	// Controle de montagem
	useEffect(() => {
		stateRef.current.isMounted = true;
		return () => {
			stateRef.current.isMounted = false;
			if (batchTimeoutRef.current) {
				clearTimeout(batchTimeoutRef.current);
			}
		};
	}, []);

	// Processa operações em batch
	const flushOperations = useCallback(() => {
		if (!stateRef.current.isMounted) return;

		const { toAdd, toUpdate, toRemove } = pendingOpsRef.current;
		if (toAdd.length === 0 && toUpdate.length === 0 && toRemove.length === 0) {
			return;
		}

		// Pega cópias e limpa
		const addOps = [...toAdd];
		const updateOps = [...toUpdate];
		const removeOps = [...toRemove];
		pendingOpsRef.current = { toAdd: [], toUpdate: [], toRemove: [] };

		setActiveBreaks(prev => {
			let breaks = [...prev];

			// Remove
			if (removeOps.length > 0) {
				const removeSet = new Set(removeOps.map(id =>
					typeof id === 'string' ? parseInt(id, 10) : id
				));
				breaks = breaks.filter(b => {
					const breakId = typeof b?.id === 'string' ? parseInt(b.id, 10) : b?.id;
					return !removeSet.has(breakId);
				});
			}

			// Update
			if (updateOps.length > 0) {
				const updateMap = new Map(updateOps.map(b => [b.id, b]));
				breaks = breaks.map(b => {
					const updated = updateMap.get(b?.id);
					return updated ? formatBreakItem(updated) : b;
				});
			}

			// Add (evita duplicatas)
			if (addOps.length > 0) {
				const existingIds = new Set(breaks.map(b => b?.id));
				const newBreaks = addOps
					.filter(b => !existingIds.has(b?.id))
					.map(formatBreakItem);
				breaks = [...breaks, ...newBreaks];
			}

			return breaks;
		});
	}, []);

	// Agenda flush com debounce - só executa se aba estiver visível
	const scheduleFlush = useCallback(() => {
		// Se aba não está visível, marca que precisa refresh quando voltar
		if (!isTabVisible()) {
			stateRef.current.needsRefresh = true;
			pendingOpsRef.current = { toAdd: [], toUpdate: [], toRemove: [] };
			return;
		}

		if (batchTimeoutRef.current) {
			clearTimeout(batchTimeoutRef.current);
		}
		batchTimeoutRef.current = setTimeout(() => {
			batchTimeoutRef.current = null;
			flushOperations();
		}, SIGNALR_BATCH_DELAY);
	}, [flushOperations]);

	// Função para buscar dados da API
	const fetchBreaks = useCallback(async (signal) => {
		if (!stateRef.current.isMounted) return;

		setLoading(true);

		try {
			let url = "break-work/all-active";
			const query = stateRef.current.teamIdsQuery;
			if (query) {
				url += "?teamids=" + query;
			}

			const response = await Api.get(url, { signal });

			if (!stateRef.current.isMounted) return;

			if (response?.data?.success) {
				const data = Array.isArray(response.data.data) ? response.data.data : [];
				setActiveBreaks(data.map(formatBreakItem));
			} else {
				const { intl: intlRef } = stateRef.current;
				setNotification({
					variant: "error",
					message: response?.data?.errors?.[0] || intlRef?.formatMessage?.({ id: "process.error" }) || "Erro"
				});
			}
		} catch (err) {
			if (err.name === 'AbortError' || err.name === 'CanceledError') {
				return;
			}
			if (stateRef.current.isMounted) {
				const { intl: intlRef } = stateRef.current;
				setNotification({
					variant: "error",
					message: err.response?.data?.errors?.[0] || intlRef?.formatMessage?.({ id: "process.error" }) || "Erro"
				});
			}
		} finally {
			if (stateRef.current.isMounted) {
				setLoading(false);
			}
		}
	}, []);

	// Handler para quando a aba volta ao foco - recarrega dados se necessário
	useEffect(() => {
		const handleVisibilityChange = () => {
			if (!document.hidden && stateRef.current.needsRefresh && stateRef.current.isMounted) {
				stateRef.current.needsRefresh = false;
				// Limpa operações pendentes e recarrega tudo
				pendingOpsRef.current = { toAdd: [], toUpdate: [], toRemove: [] };
				if (batchTimeoutRef.current) {
					clearTimeout(batchTimeoutRef.current);
					batchTimeoutRef.current = null;
				}
				fetchBreaks();
			}
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, [fetchBreaks]);

	// Busca dados iniciais e quando teamIdsQuery muda
	useEffect(() => {
		const abortController = new AbortController();
		fetchBreaks(abortController.signal);

		return () => {
			abortController.abort();
		};
	}, [teamIdsQuery, fetchBreaks]);

	// Registra handlers SignalR
	useEffect(() => {
		if (!connection || typeof connection.on !== 'function') return;
		if (stateRef.current.isInitialized) return;

		const handleBreakStarted = (breakWork) => {
			if (!stateRef.current.isMounted || !breakWork) return;

			// Se aba não está visível, marca para refresh e ignora evento
			if (!isTabVisible()) {
				stateRef.current.needsRefresh = true;
				return;
			}

			const currentTeamIds = stateRef.current.teamIdsArray;
			const hasFilter = Array.isArray(currentTeamIds) && currentTeamIds.length > 0;

			let shouldAdd = !hasFilter;
			if (hasFilter) {
				const breakTeamIds = breakWork.user?.techinicalteamusers?.map(ut => ut?.techinicalteam?.id).filter(id => id != null) || [];
				shouldAdd = breakTeamIds.some(id =>
					currentTeamIds.some(tid => Number(tid) === Number(id))
				);
			}

			if (shouldAdd) {
				pendingOpsRef.current.toAdd.push(breakWork);
				scheduleFlush();
			}
		};

		const handleBreakUpdated = (breakWork) => {
			if (!stateRef.current.isMounted || !breakWork?.id) return;

			// Se aba não está visível, marca para refresh e ignora evento
			if (!isTabVisible()) {
				stateRef.current.needsRefresh = true;
				return;
			}

			pendingOpsRef.current.toUpdate.push(breakWork);
			scheduleFlush();
		};

		const handleBreakEnded = (id) => {
			if (!stateRef.current.isMounted || id == null) return;

			// Se aba não está visível, marca para refresh e ignora evento
			if (!isTabVisible()) {
				stateRef.current.needsRefresh = true;
				return;
			}

			pendingOpsRef.current.toRemove.push(id);
			scheduleFlush();
		};

		const handleBreakDeleted = (id) => {
			if (!stateRef.current.isMounted || id == null) return;

			// Se aba não está visível, marca para refresh e ignora evento
			if (!isTabVisible()) {
				stateRef.current.needsRefresh = true;
				return;
			}

			pendingOpsRef.current.toRemove.push(id);
			scheduleFlush();
		};

		const handleReconnected = async () => {
			try {
				if (stateRef.current.isMounted && connection.state === signalR.HubConnectionState.Connected) {
					await connection.invoke("JoinBreakRoom");
					setConnected(true);
				}
			} catch {
				setConnected(false);
			}
		};

		const handleReconnecting = () => {
			if (stateRef.current.isMounted) setConnected(false);
		};

		const handleDisconnected = () => {
			if (stateRef.current.isMounted) setConnected(false);
		};

		const init = async () => {
			try {
				if (!connection.state) return;

				// Remove handlers antigos
				try {
					connection.off("BreakStarted", handleBreakStarted);
					connection.off("BreakUpdated", handleBreakUpdated);
					connection.off("BreakEnded", handleBreakEnded);
					connection.off("BreakDeleted", handleBreakDeleted);
				} catch { }

				connection.on("BreakStarted", handleBreakStarted);
				connection.on("BreakUpdated", handleBreakUpdated);
				connection.on("BreakEnded", handleBreakEnded);
				connection.on("BreakDeleted", handleBreakDeleted);

				if (connection.onreconnected) connection.onreconnected(handleReconnected);
				if (connection.onreconnecting) connection.onreconnecting(handleReconnecting);
				if (connection.onclose) connection.onclose(handleDisconnected);

				if (connection.state === signalR.HubConnectionState.Connected) {
					try {
						await connection.invoke("JoinBreakRoom");
						if (stateRef.current.isMounted) setConnected(true);
					} catch {
						if (stateRef.current.isMounted) setConnected(false);
					}
				}

				stateRef.current.isInitialized = true;
			} catch {
				if (stateRef.current.isMounted) setConnected(false);
			}
		};

		init();

		return () => {
			stateRef.current.isInitialized = false;
			if (batchTimeoutRef.current) {
				clearTimeout(batchTimeoutRef.current);
				batchTimeoutRef.current = null;
			}
			pendingOpsRef.current = { toAdd: [], toUpdate: [], toRemove: [] };

			if (connection && typeof connection.off === 'function') {
				connection.off("BreakStarted", handleBreakStarted);
				connection.off("BreakUpdated", handleBreakUpdated);
				connection.off("BreakEnded", handleBreakEnded);
				connection.off("BreakDeleted", handleBreakDeleted);

				if (connection.state === signalR.HubConnectionState.Connected) {
					connection.invoke("LeaveBreakRoom").catch(() => { });
				}
			}
		};
	}, [connection, scheduleFlush]);

	// Função para limpar notificação
	const clearNotification = useCallback(() => {
		setNotification(null);
	}, []);

	// Retorna estado diretamente - componente pai só precisa usar esses valores
	return {
		activeBreaks,
		loading,
		connected,
		notification,
		clearNotification
	};
}
